import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const BulkOrderForm = () => {
  const [orderData, setOrderData] = useState({
    full_name: "",
    email: "",
    contact_number: "",
    organization: "",
    order_purpose: "",
    delivery_date: "",
    delivery_address: "",
    quantity: "",
    additional_message: "",
  });

  const [orderPurposes, setOrderPurposes] = useState([]);
  const [loadingPurposes, setLoadingPurposes] = useState(true);
  const [purposeError, setPurposeError] = useState(null);

  // Pipeline Operation Hooks
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState({ type: "", text: "" });

  const API_BASE = "http://localhost:5000/api";

  // Fetch critical pipeline data structures on runtime execution mount
  useEffect(() => {
    const fetchOrderPurposes = async () => {
      try {
        const response = await axios.get(`${API_BASE}/order-purposes`);
        setOrderPurposes(response.data);
        setPurposeError(null);
      } catch (error) {
        console.error("Infrastructure pipeline error:", error);
        setPurposeError("Failed to fetch order allocation parameters.");

        // Quality Fallback entries to keep systemic execution uninterrupted
        setOrderPurposes([
          { _id: "p_corp", name: "Corporate Summit / Event" },
          { _id: "p_horeca", name: "HORECA Procurement Line" },
          { _id: "p_edu", name: "Educational Institute Fleet" },
          { _id: "p_med", name: "Medical Cluster Supply" },
        ]);
      } finally {
        setLoadingPurposes(false);
      }
    };

    fetchOrderPurposes();
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Strict parsing for mathematical quantity values
    const targetValue =
      name === "quantity"
        ? value === ""
          ? ""
          : Math.abs(parseInt(value, 10))
        : value;

    setOrderData((prev) => ({
      ...prev,
      [name]: targetValue,
    }));
  };

  // Real-time Director Dashboard Estimation Calculator
  const computeLiveQuote = (units) => {
    if (!units || isNaN(units)) return { rate: 0, total: 0, segment: "None" };

    // Core commercial scale tier limits mapping logic
    let rate = 6.0;
    let segment = "Retail Baseline";

    if (units >= 500) {
      rate = 4.2; // 250ml High Velocity Segment Limit
      segment = "Peak Velocity B2B Tier";
    } else if (units >= 200) {
      rate = 7.5; // 500ml standard tier optimization
      segment = "Standard Corporate Fleet";
    } else if (units >= 100) {
      rate = 14.8;
      segment = "Enterprise Volume Scale";
    } else if (units >= 50) {
      rate = 23.0;
      segment = "Disaster/Relief Strategic Unit";
    }

    return {
      rate: rate.toFixed(2),
      total: (units * rate).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      segment,
    };
  };

  const liveQuote = computeLiveQuote(orderData.quantity);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", text: "" });

    // Validate date framework parameters logic
    const requestedDate = new Date(orderData.delivery_date);
    const evaluationThreshold = new Date();
    evaluationThreshold.setDate(evaluationThreshold.getDate() + 2); // Minimum 48-hour pipeline logistics buffer

    if (requestedDate < evaluationThreshold) {
      setFormStatus({
        type: "danger",
        text: "Logistics Warning: Bulk delivery configurations require a minimum 48-hour processing pipeline verification window.",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/bulk-orders`, orderData);
      setFormStatus({
        type: "success",
        text:
          response.data.message ||
          "Enterprise lead pipeline synchronized successfully. Our deployment team will initiate contact.",
      });

      // Clear data states cleanly upon validation execution
      setOrderData({
        full_name: "",
        email: "",
        contact_number: "",
        organization: "",
        order_purpose: "",
        delivery_date: "",
        delivery_address: "",
        quantity: "",
        additional_message: "",
      });
    } catch (error) {
      const fallbackError =
        error.response?.data?.message ||
        "Critical structural pipeline commit execution fault. Retry contact synchronization later.";
      setFormStatus({ type: "danger", text: fallbackError });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="position-relative py-5 min-vh-100 d-flex align-items-center"
      style={{
        background:
          "linear-gradient(135deg, var(--brand-charcoal) 0%, var(--brand-deep-blue) 100%)",
        color: "var(--brand-pure-white)",
      }}
    >
      {/* Background Decorative Resource Matrix Elements */}
      <div
        className="position-absolute rounded-circle opacity-25"
        style={{
          width: "650px",
          height: "650px",
          top: "-10%",
          left: "-15%",
          background: "var(--brand-sky-blue)",
          filter: "blur(160px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle opacity-20"
        style={{
          width: "450px",
          height: "450px",
          bottom: "-10%",
          right: "-10%",
          background: "var(--brand-copper)",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />

      <div className="container position-relative z-1 py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-9 col-xl-8">
            {/* Header Content Module */}
            <div className="text-center mb-5">
              <span
                className="text-uppercase fw-bold px-3 py-1 rounded-pill small d-inline-block mb-3"
                style={{
                  backgroundColor: "var(--brand-sky-blue)",
                  color: "var(--brand-deep-blue)",
                  fontSize: "0.72rem",
                  letterSpacing: "1.5px",
                }}
              >
                Commercial Procurement Operations Hub
              </span>
              <h1 className="display-5 fw-bold text-white mb-2">
                High-Volume Fleet Allocation Request
              </h1>
              <p
                className="text-white-50 mx-auto"
                style={{ maxWidth: "600px", fontSize: "0.95rem" }}
              >
                Configure comprehensive supply chain channels for enterprise
                distribution clusters, infrastructure deployments, or HORECA
                frameworks.
              </p>
            </div>

            {/* Premium Immersive Glass Layout Workstation Form Block */}
            <motion.div
              className="card backdrop-blur border-1 rounded-4 p-4 p-md-5"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: "spring", stiffness: 90, damping: 20 }}
              style={{
                background: "rgba(255, 255, 255, 0.04)",
                borderColor: "rgba(255, 255, 255, 0.08)",
                boxShadow: "0 30px 70px rgba(0, 0, 0, 0.35)",
              }}
            >
              <AnimatePresence mode="wait">
                {formStatus.text && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`alert alert-${formStatus.type} text-center fw-medium border-0 mb-4 py-3 rounded-3 shadow-sm`}
                    style={{ fontSize: "0.9rem" }}
                  >
                    <i
                      className={`bi ${
                        formStatus.type === "success"
                          ? "bi-check-circle-fill me-2"
                          : "bi-exclamation-triangle-fill me-2"
                      }`}
                    ></i>
                    {formStatus.text}
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit}>
                <h4
                  className="h6 text-uppercase tracking-wider fw-bold mb-4 text-start"
                  style={{ color: "var(--brand-mineral-aqua)" }}
                >
                  <i className="bi bi-person-badge-fill me-2"></i>Primary
                  Identity Registry
                </h4>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-white-50">
                      Authorized Official Representative Name
                    </label>
                    <input
                      type="text"
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="full_name"
                      placeholder="e.g., Jane Doe"
                      value={orderData.full_name}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                      }}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-white-50">
                      Corporate Communications Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="email"
                      placeholder="official@organization.org"
                      value={orderData.email}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                      }}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-white-50">
                      Direct Contact Hot-line Number
                    </label>
                    <input
                      type="tel"
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="contact_number"
                      placeholder="Operational terminal mobile line"
                      value={orderData.contact_number}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                      }}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-white-50">
                      Registered Corporate Entity / Organization
                    </label>
                    <input
                      type="text"
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="organization"
                      placeholder="Legal Entity Corporate Name"
                      value={orderData.organization}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                      }}
                    />
                  </div>
                </div>

                <h4
                  className="h6 text-uppercase tracking-wider fw-bold mb-4 pt-2 text-start"
                  style={{ color: "var(--brand-mineral-aqua)" }}
                >
                  <i className="bi bi-box-seam-fill me-2"></i>Logistics and
                  Operational Sizing Parameters
                </h4>

                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-white-50">
                      Supply Allocation Target Purpose
                    </label>
                    {loadingPurposes ? (
                      <div
                        className="form-control text-white-50 border-0 py-2.5 px-3 rounded-3"
                        style={{ background: "rgba(255, 255, 255, 0.06)" }}
                      >
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Parsing targets...
                      </div>
                    ) : (
                      <select
                        className="form-select text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                        name="order_purpose"
                        value={orderData.order_purpose}
                        onChange={handleChange}
                        required
                        style={{
                          background: "rgba(255, 255, 255, 0.06)",
                          borderBottom: "2px solid rgba(255,255,255,0.15)",
                          cursor: "pointer",
                        }}
                      >
                        <option
                          value=""
                          disabled
                          className="bg-dark text-white-50"
                        >
                          Select Operational Target Vector
                        </option>
                        {orderPurposes.map((purpose) => (
                          <option
                            key={purpose._id}
                            value={purpose.name}
                            className="bg-dark text-white"
                          >
                            {purpose.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label small fw-semibold text-white-50">
                      Required Fleet On-Site Delivery Date
                    </label>
                    <input
                      type="date"
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="delivery_date"
                      value={orderData.delivery_date}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                        colorScheme: "dark",
                      }}
                    />
                  </div>

                  <div className="col-12 col-md-8">
                    <label className="form-label small fw-semibold text-white-50">
                      High-Volume Fleet Sizing Quantity (Bottles Count)
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none fw-bold"
                      name="quantity"
                      placeholder="Specify absolute total numerical resource footprint"
                      value={orderData.quantity}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                        color: "var(--brand-mineral-aqua) !important",
                      }}
                    />
                  </div>

                  {/* Real-time Director Price Matrix Projection Console Panel */}
                  <div className="col-12">
                    <AnimatePresence>
                      {orderData.quantity > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="p-3 rounded-3 border d-flex flex-wrap align-items-center justify-content-between gap-3 bg-white"
                          style={{ borderColor: "rgba(255,255,255,0.15)" }}
                        >
                          <div>
                            <span
                              className="badge text-uppercase mb-1"
                              style={{
                                backgroundColor: "var(--brand-sky-blue)",
                                color: "var(--brand-deep-blue)",
                                fontSize: "0.65rem",
                              }}
                            >
                              {liveQuote.segment}
                            </span>
                            <div className="text-dark small fw-medium">
                              Live System Cost Engine Projections
                            </div>
                          </div>
                          <div className="text-end">
                            <span className="text-muted small me-3">
                              Rate: ₹{liveQuote.rate}/unit
                            </span>
                            <span className="h5 fw-bold text-success m-0">
                              Est. Total: ₹{liveQuote.total}
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="col-12">
                    <label className="form-label small fw-semibold text-white-50">
                      Complete Legal Infrastructure Delivery Address
                    </label>
                    <textarea
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="delivery_address"
                      rows="3"
                      placeholder="Provide verified geographical site drop matrix guidelines..."
                      value={orderData.delivery_address}
                      onChange={handleChange}
                      required
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                        resize: "none",
                      }}
                    />
                  </div>

                  <div className="col-12 mb-2">
                    <label className="form-label small fw-semibold text-white-50">
                      Additional Distribution Pipeline Directives / Notes
                    </label>
                    <textarea
                      className="form-control text-white border-0 py-2.5 px-3 rounded-3 shadow-none"
                      name="additional_message"
                      rows="3"
                      placeholder="Optional technical instructions regarding labeling requirements, custom packaging configurations, or terminal gate access guidelines..."
                      value={orderData.additional_message}
                      onChange={handleChange}
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        borderBottom: "2px solid rgba(255,255,255,0.15)",
                        resize: "none",
                      }}
                    />
                  </div>
                </div>

                <div className="d-grid mt-4">
                  <motion.button
                    type="submit"
                    className="btn fw-bold py-3 text-uppercase tracking-wider rounded-3 border-0 transition-all shadow"
                    disabled={isSubmitting}
                    whileHover={{
                      scale: 1.01,
                      backgroundColor: "var(--brand-mineral-aqua)",
                    }}
                    whileTap={{ scale: 0.99 }}
                    style={{
                      background: "var(--brand-sky-blue)",
                      color: "var(--brand-deep-blue)",
                      letterSpacing: "1.2px",
                      boxShadow: "0 10px 25px rgba(185, 230, 245, 0.2)",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Synchronizing Supply Vector Contract...
                      </>
                    ) : (
                      "Lock Supply Matrix Request"
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BulkOrderForm;
